import Dropzone from 'dropzone';

import { allowedImageUploads, isJPEG2000File, sanitizeFileName } from './utils';

/** ProductImageUploadView - provides the look-and-feel of an upload view */
export class ProductImageUploadView implements MerchantosProductImageUploadView {
  private id = '';

  private readonly previewTemplate =
    '<div class="dz-preview dz-file-preview"><div class="dz-details"><span class="dz-percent-complete"></span><span class="dz-details-text">of <span class="dz-filename" data-dz-name notranslate></span> complete</span></div><div class="dz-progress"><span class="dz-upload" data-dz-uploadprogress></span></div><div class="dz-filesize" data-dz-size notranslate></div><div class="dz-errormessage" data-dz-errormessage notranslate></div></div>';

  private readonly dataXmlTemplate: Record<ImageType, string> = {
    Image: '<?xml version="1.0"?><Image><filename/><description/><ordering/></Image>',
    WorkorderImage:
      '<?xml version="1.0"?><WorkorderImage><filename/><description/><ordering/></WorkorderImage>',
  };

  private completed = 0;

  private $container: JQuery;

  private uploadId = 0;

  private currentUploads: { [key: string]: number } = {};

  private maxImages = 12;

  private uploadsInProgress = 0;

  private readonly supportedMimeTypes = [
    ...Object.values(allowedImageUploads)
      .map((item) => item.mimeType)
      .flat(),
  ];

  private options: {
    type: string;
    imageType: ImageType;
    hideProgress: boolean;
    nextOrder: number;
    productMatrix: boolean;
    currentImages: number;
  };

  private dropZone!: Dropzone;

  constructor($imageContainer: JQuery, options: Partial<ProductImageUploadView['options']>) {
    this.$container = $imageContainer;
    this.options = {
      type: 'Item',
      imageType: 'Image',
      hideProgress: false,
      nextOrder: 0,
      productMatrix: false,
      currentImages: 0,
      ...options,
    };
    this.createDropzone();
  }

  createDropzone: MerchantosProductImageUploadView['createDropzone'] = () => {
    const imageUsage = window.merchantos.session.imageUsage?.Usage;

    if (
      imageUsage &&
      imageUsage.totalImageSizeHardLimitReached === 'false' &&
      imageUsage.imageCountHardLimitReached === 'false'
    ) {
      // currently only support one dropzone per view; this can easily be expanded if necessary
      let $dropZone;
      if (this.$container) {
        $dropZone = this.$container.find('.upload');
      } else {
        $dropZone = $('.upload');
      }

      if ($dropZone.length === 0) {
        return;
      }
      const id = $dropZone.attr('id');

      this.id = `#${id || 'view_image_upload'}`;

      // setup the base view for the upload
      $dropZone.find('.hide').on('click', this.collapse);

      // init dropzone
      // warning: although Dropzone can be attached as a jQuery plugin don't use that
      // form otherwise binding to Dropzone events won't work
      this.dropZone = new Dropzone(this.id, {
        url: `/API/Account/${window.merchantos.account.id}/${this.options.imageType}`,
        paramName: 'image',
        parallelUploads: 12,
        createImageThumbnails: false,
        clickable: `.image ${this.id}.upload button`,
        previewTemplate: this.previewTemplate,
        autoProcessQueue: true,
        previewsContainer: `${this.id} .uploads > .uploads-progress`,
        headers: { Accept: 'application/xml' },
        renameFilename: sanitizeFileName,
        acceptedFiles: Object.values(allowedImageUploads)
          .map((item) => item.extension)
          .flat()
          .join(', '), // This will restrict the filed allowed for upload
        error: (file, errorMessage) => {
          const message =
            typeof errorMessage === 'string' && errorMessage.includes('xml')
              ? $($.parseXML(errorMessage)).find('message')[0].textContent
              : errorMessage;
          console.error(message); // Show the error message in the console for debugging
          $(file.previewElement).remove();
          this.$container.trigger('errorOccurred', { file });
          // Remove the current failed file
          delete this.currentUploads[file.name];
        },
      });

      window.blockDropzoneFilesizeTranslation();

      // setup dropZone callbacks
      this.dropZone.on('addedfile', this.fileAdded);
      this.dropZone.on('uploadprogress', this.percentComplete);
      this.dropZone.on('success', this.uploadComplete);
      this.dropZone.on('selectedfiles', this.uploadStarting);
      this.dropZone.on('reset', this.allUploadsComplete);
      this.dropZone.on('error', this.uploadError);
      this.dropZone.on('sending', this.appendFileData);
      if (this.options.hideProgress) {
        $dropZone.find('.uploads').hide();
      }
    }
  };

  uploadStarting: MerchantosProductImageUploadView['uploadStarting'] = (files) => {
    // Only add the class when not uploading from the modal
    // (only do it when we're uploading directly from the Item page)
    const uploadsContainer = $(this.id);
    if (!uploadsContainer.hasClass('upload-modal')) {
      uploadsContainer.addClass('uploading');
    }

    const $uploads = $(this.id).find('.uploads');

    $uploads.find('.dz-uploads-complete').text(this.completed);

    const total = Math.min(this.dropZone.files.length + files.length, this.maxImages);
    $uploads.find('.dz-uploads-total').text(total);
    $('.image-limit').hide();
  };

  allUploadsComplete: MerchantosProductImageUploadView['allUploadsComplete'] = () => {
    this.completed = 0;

    // Only add the class when not uploading from the modal
    // (only do it when we're uploading directly from the Item page)
    const uploadsContainer = $(this.id);
    if (!uploadsContainer.hasClass('upload-modal')) {
      uploadsContainer.removeClass('uploading');
    }

    this.$container.trigger('alluploadscomplete');
    this.uploadId = 0;
  };

  percentComplete: MerchantosProductImageUploadView['percentComplete'] = (file) => {
    const $previewElement = $(file.previewElement);
    const $percentComplete = $previewElement.find('.dz-percent-complete');
    $percentComplete.text(`${file.upload!.progress}% `);
    this.$container.trigger('percentcomplete', {
      uploadId: this.currentUploads[file.name],
      progress: file.upload?.progress,
    });
  };

  uploadError: MerchantosProductImageUploadView['uploadError'] = () => {
    //	$(file.previewElement).find('.dz-upload').addClass('error');
  };

  fileAdded: MerchantosProductImageUploadView['fileAdded'] = (file) => {
    if (
      this.options.currentImages + this.completed + this.uploadsInProgress >= this.maxImages ||
      !this.supportedMimeTypes.includes(file.type)
    ) {
      this.cancelUpload(file);
      return false;
    }
    this.uploadsInProgress += 1;

    // shorten the filename to 16 characters
    const $previewElement = $(file.previewElement);
    const $fileName = $previewElement.find('.dz-filename');
    const splitFileName = $fileName.text().split('.');
    const shortFileName = splitFileName[0].shorten(16);

    // As browsers don't support JPEG2000, when JPEG2000 file is uploaded we request .webp file instead from the server to display
    const extension =
      splitFileName.length > 1 && isJPEG2000File({ file })
        ? allowedImageUploads.WEBP.extension[0].replace('.', '')
        : splitFileName.pop();
    $fileName.text(`${shortFileName}.${extension}`);

    // strip out the file size; we don't need it
    const $fileSize = $previewElement.find('.dz-filesize');
    $fileSize.html('');

    this.currentUploads[file.name] = this.uploadId++;
    this.$container.trigger('fileadded', {
      filename: `${shortFileName}.${extension}`,
      uploadId: this.currentUploads[file.name],
      file,
    });

    this.percentComplete(file);
  };

  uploadComplete: MerchantosProductImageUploadView['uploadComplete'] = (file, response) => {
    this.uploadsInProgress -= 1;

    this.dropZone.removeFile(file);
    this.$container.find('.dz-uploads-complete').text(++this.completed);

    this.$container.trigger('uploadcomplete', {
      uploadId: this.currentUploads[file.name],
      response,
    });

    delete this.currentUploads[file.name];

    // If there are no queued uploads and at-least 1 upload is completed show that as preview
    if (Object.keys(this.currentUploads).length === 0 && this.completed >= 1) {
      this.$container.trigger('alluploadscomplete');
    }
  };

  appendFileData: MerchantosProductImageUploadView['appendFileData'] = (file, _xhr, formData) => {
    const id = $('div#view')[0].view!.record_id!; // the id is stored in the panel element
    const xml = this.getDataXml(file.name, '', id.toString(), this.options.nextOrder++);
    const xmlString = new XMLSerializer().serializeToString(xml[0]);

    formData.append('data', xmlString);
  };

  expand: MerchantosProductImageUploadView['expand'] = (event) => {
    if (event) {
      event.preventDefault();
    }
    this.$container.find('.prompt').addClass('hide');
    // since we need the upload region to be hidden at first and jQuery fadeIn sets the display to
    $(this.id).css('opacity', 0).animate({ opacity: 1 }).removeClass('hide');
  };

  collapse: MerchantosProductImageUploadView['collapse'] = (event?: Event) => {
    if (event) {
      event.preventDefault();
    }
    $(this.id).addClass('hide');
    this.$container.find('.prompt').css('opacity', 0).animate({ opacity: 1 }).removeClass('hide');
  };

  getDataXml: MerchantosProductImageUploadView['getDataXml'] = (
    filename: string,
    description: string,
    id: string,
    order: number
  ) => {
    const $xml = $($.parseXML(this.dataXmlTemplate[this.options.imageType]));

    $xml.find('filename').text(filename);
    $xml.find('description').text(description);

    let $id: JQuery<HTMLElement>;

    if (this.options.type === 'Workorder') {
      $id = $($.parseXML('<workorderID/>')).find('workorderID').text(id);
    } else if (this.options.productMatrix) {
      $id = $($.parseXML('<itemMatrixID/>')).find('itemMatrixID').text(id);
    } else {
      $id = $($.parseXML('<itemID/>')).find('itemID').text(id);
    }

    $xml.find(this.options.imageType).append($id);
    $xml.find('ordering').text(order);

    return $xml;
  };

  cancelUpload: MerchantosProductImageUploadView['cancelUpload'] = (file) => {
    this.dropZone.removeFile(file);
  };

  disable: MerchantosProductImageUploadView['disable'] = () => {
    // Dropzone doesn't clean up the hidden file input so this method can be called
    // to cleanup when the dropzone is no longer needed
    const $hiddenFileInputs = $('body').find('input[type*="file"]');
    if ($hiddenFileInputs.length) {
      // eslint-disable-next-line func-names
      $.each($hiddenFileInputs, function () {
        $(this).remove();
      });
    }

    if (this.dropZone) {
      this.dropZone.disable();
      // @ts-expect-error ts-migrate(2790) FIXME: The operand of a 'delete' operator must be optiona... Remove this comment to see the full error message
      delete this.dropZone;
    }
  };
}
